import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  CheckCircle, XCircle, Clock, User, Phone, MapPin, Flag,
  Footprints, CalendarDays, UserCheck, Inbox, ChevronRight, Eye
} from 'lucide-react';
import PaginationBar, { usePagedRows } from '@/components/PaginationBar';

const API = (window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "");

const STATUS_CONFIG = {
  pending:  { label: 'Pendiente',  color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',  icon: Clock },
  approved: { label: 'Aprobado',   color: 'bg-green-500/20 text-green-400 border-green-500/30',     icon: CheckCircle },
  rejected: { label: 'Rechazado',  color: 'bg-red-500/20 text-red-400 border-red-500/30',           icon: XCircle },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function PlayerCard({ player, onApprove, onReject, onView }) {
  const age = player.birth_date
    ? Math.floor((new Date() - new Date(player.birth_date)) / (365.25 * 24 * 3600 * 1000))
    : null;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`player-card-${player.id}`}>
      <div className="flex items-start justify-between gap-3">
        {/* Avatar + name */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-gray-400" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">
              {player.first_name} {player.last_name}
            </h3>
            <p className="text-sm text-gray-500">
              {[player.position, age ? `${age} años` : null].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <StatusBadge status={player.validation_status || 'pending'} />
      </div>

      {/* Details */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        {player.phone && (
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{player.phone}</span>
          </div>
        )}
        {player.nationality && (
          <div className="flex items-center gap-2 text-gray-600">
            <Flag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{player.nationality}</span>
          </div>
        )}
        {player.community_passport && (
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">Pasaporte EU: {player.community_passport}</span>
          </div>
        )}
        {player.preferred_foot && (
          <div className="flex items-center gap-2 text-gray-600">
            <Footprints className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">Pie: {player.preferred_foot}</span>
          </div>
        )}
        {player.brought_by && (
          <div className="flex items-center gap-2 text-gray-600 col-span-2">
            <UserCheck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">Traído por: <strong>{player.brought_by}</strong></span>
          </div>
        )}
        {player.created_at && (
          <div className="flex items-center gap-2 text-gray-400 col-span-2 text-xs">
            <CalendarDays className="w-3 h-3 flex-shrink-0" />
            <span>Recibido: {new Date(player.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={() => onView(player)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid={`view-btn-${player.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
          Ver datos
        </button>

        {player.validation_status === 'pending' && (
          <>
            <button
              onClick={() => onApprove(player)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors font-medium"
              data-testid={`approve-btn-${player.id}`}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              Aprobar
            </button>
            <button
              onClick={() => onReject(player)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors font-medium"
              data-testid={`reject-btn-${player.id}`}
            >
              <XCircle className="w-3.5 h-3.5" />
              Rechazar
            </button>
          </>
        )}
        {player.validation_status === 'rejected' && player.rejection_reason && (
          <span className="text-xs text-red-500 italic truncate">Motivo: {player.rejection_reason}</span>
        )}
      </div>
    </div>
  );
}

function PlayerDetailModal({ player, onClose }) {
  if (!player) return null;
  const fields = [
    { label: 'Nombre completo', value: `${player.first_name} ${player.last_name}` },
    { label: 'Fecha de Nacimiento', value: player.birth_date },
    { label: 'Cédula', value: player.document },
    { label: 'Pasaporte', value: player.passport },
    { label: 'Pasaporte Comunitario', value: player.community_passport },
    { label: 'Nacionalidad', value: player.nationality },
    { label: 'Teléfono', value: player.phone },
    { label: 'Email', value: player.email },
    { label: 'Redes Sociales', value: player.social_media },
    { label: 'Dirección', value: player.address },
    { label: 'Nombre Madre', value: player.mother_name },
    { label: 'Teléfono Madre', value: player.mother_phone },
    { label: 'Nombre Padre', value: player.father_name },
    { label: 'Teléfono Padre', value: player.father_phone },
    { label: 'Tutor', value: player.tutor_name },
    { label: 'Teléfono Tutor', value: player.tutor_phone },
    { label: 'Posición Principal', value: player.position },
    { label: 'Posición Secundaria', value: player.position_secondary },
    { label: 'Pierna Hábil', value: player.preferred_foot },
    { label: 'Talle de Botines (EUR)', value: player.shoe_size },
    { label: 'Altura', value: player.height ? `${player.height}m` : null },
    { label: 'Peso', value: player.weight ? `${player.weight}kg` : null },
    { label: 'Mutualista', value: player.medical_coverage },
    { label: 'Trayectoria de Clubes', value: player.club_trajectory },
    { label: 'Scout que trajo', value: player.brought_by },
  ].filter(f => f.value);

  return (
    <Dialog open={!!player} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Datos completos: {player.first_name} {player.last_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-2">
          {fields.map(f => (
            <div key={f.label}>
              <p className="text-xs text-gray-400 uppercase tracking-wide">{f.label}</p>
              <p className="text-sm font-medium text-gray-900">{f.value}</p>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormulariosRecibidos() {
  const { token } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [viewTarget, setViewTarget] = useState(null);
  const [processing, setProcessing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/pending-players`);
      setPlayers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('[formularios-recibidos]', err);
      const msg = err.response?.data?.detail || err.message || 'desconocido';
      toast.error(`Error al cargar formularios: ${msg}`);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const handleApprove = async (player) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/api/pending-players/${player.id}/approve`, {}, { headers });
      toast.success(`${player.first_name} ${player.last_name} aprobado`);
      fetchPlayers();
    } catch {
      toast.error('Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/api/pending-players/${rejectTarget.id}/reject`, { rejection_reason: rejectionReason }, { headers });
      toast.success(`${rejectTarget.first_name} ${rejectTarget.last_name} rechazado`);
      setRejectTarget(null);
      setRejectionReason('');
      fetchPlayers();
    } catch {
      toast.error('Error al rechazar');
    } finally {
      setProcessing(false);
    }
  };

  const filtered = players.filter(p => (p.validation_status || 'pending') === activeTab);
  const counts = {
    pending:  players.filter(p => (p.validation_status || 'pending') === 'pending').length,
    approved: players.filter(p => p.validation_status === 'approved').length,
    rejected: players.filter(p => p.validation_status === 'rejected').length,
  };

  const tabs = [
    { key: 'pending',  label: 'Pendientes',  count: counts.pending },
    { key: 'approved', label: 'Aprobados',   count: counts.approved },
    { key: 'rejected', label: 'Rechazados',  count: counts.rejected },
  ];

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Inbox className="w-6 h-6 text-gray-700" />
              <h1 className="text-2xl font-bold text-gray-900">Formularios Recibidos</h1>
              {counts.pending > 0 && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full border border-yellow-200" data-testid="pending-badge">
                  {counts.pending} pendiente{counts.pending !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm">Jugadores que llegaron por el formulario público de registro</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6" data-testid="tabs-container">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab.key ? 'bg-gray-100 text-gray-700' : 'bg-gray-200 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm">Cargando...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400" data-testid="empty-state">
            <Inbox className="w-10 h-10 mb-3 opacity-40" />
            <p className="text-sm">
              {activeTab === 'pending' ? 'No hay formularios pendientes de revisión' :
               activeTab === 'approved' ? 'No hay jugadores aprobados aún' :
               'No hay formularios rechazados'}
            </p>
          </div>
        ) : (
          <PaginatedFormsList
            filtered={filtered}
            handleApprove={handleApprove}
            setRejectTarget={setRejectTarget}
            setRejectionReason={setRejectionReason}
            setViewTarget={setViewTarget}
            activeTab={activeTab}
          />
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={() => setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar jugador</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            ¿Estás seguro de rechazar a <strong>{rejectTarget?.first_name} {rejectTarget?.last_name}</strong>?
          </p>
          <div className="space-y-2">
            <Label>Motivo (opcional)</Label>
            <Textarea
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="Ej: No cumple el perfil buscado, datos incompletos..."
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={processing}
              data-testid="confirm-reject-btn"
            >
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View detail modal */}
      <PlayerDetailModal player={viewTarget} onClose={() => setViewTarget(null)} />
    </Layout>
  );
}

function PaginatedFormsList({ filtered, handleApprove, setRejectTarget, setRejectionReason, setViewTarget, activeTab }) {
  const { paged, pagination } = usePagedRows(filtered, { initialPageSize: 20 });
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="players-grid" key={activeTab}>
        {paged.map(player => (
          <PlayerCard
            key={player.id}
            player={player}
            onApprove={handleApprove}
            onReject={(p) => { setRejectTarget(p); setRejectionReason(''); }}
            onView={(p) => setViewTarget(p)}
          />
        ))}
      </div>
      <PaginationBar {...pagination} />
    </>
  );
}

